import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  pushSupported, currentPermission, isSubscribed,
  subscribeToPush, unsubscribeFromPush, needsIosInstall,
} from "@/lib/push";

export default function PushToggle({ compact = false }) {
  const { t } = useTranslation();
  const [supported, setSupported] = useState(true);
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const refresh = async () => {
    setSupported(pushSupported());
    setPerm(currentPermission());
    setSubscribed(await isSubscribed());
    setIosHint(needsIosInstall());
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        toast.success(t("push.disabled", { defaultValue: "Notifications disabled" }));
      } else {
        await subscribeToPush();
        toast.success(t("push.enabled", { defaultValue: "Notifications enabled — you'll get alerts even when the app is closed." }));
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Failed to update push subscription");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="card-nb p-4 bg-[#F4F1EA]" data-testid="push-unsupported">
        <p className="text-sm font-bold">{t("push.unsupported", { defaultValue: "Push notifications aren't supported on this browser." })}</p>
      </div>
    );
  }

  if (iosHint) {
    return (
      <div className="card-nb p-4 bg-[#FFF1D6]" data-testid="push-ios-hint">
        <div className="flex items-start gap-3">
          <Smartphone className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <div className="font-display font-black text-base">{t("push.iosTitle", { defaultValue: "Install TANTA to enable notifications" })}</div>
            <p className="text-xs text-[#4A3000] mt-1">{t("push.iosBody", { defaultValue: "On iPhone & iPad, tap the Share button then 'Add to Home Screen'. Open TANTA from the home-screen icon to enable push notifications." })}</p>
          </div>
        </div>
      </div>
    );
  }

  const blocked = perm === "denied";
  return (
    <div className={`card-nb ${compact ? "p-3" : "p-4"} ${subscribed ? "bg-[#E2F3E4]" : "bg-[#FFF1D6]"}`} data-testid="push-toggle">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {subscribed ? <Bell className="w-5 h-5 shrink-0" strokeWidth={2.5} /> : <BellOff className="w-5 h-5 shrink-0" strokeWidth={2.5} />}
          <div className="min-w-0">
            <div className="font-display font-black text-sm sm:text-base truncate">
              {subscribed
                ? t("push.onTitle", { defaultValue: "Push notifications are on" })
                : t("push.offTitle", { defaultValue: "Get notified instantly" })}
            </div>
            {!compact && (
              <div className="text-[11px] sm:text-xs text-[#4A3000] truncate">
                {blocked
                  ? t("push.blocked", { defaultValue: "Notifications are blocked — re-enable from your browser site settings." })
                  : subscribed
                    ? t("push.onBody", { defaultValue: "Friend requests, activity updates, chats, and gifts." })
                    : t("push.offBody", { defaultValue: "Tap to enable browser push for friend requests, chats and activity updates." })}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy || blocked}
          data-testid="push-toggle-btn"
          className={`btn-nb shrink-0 ${subscribed ? "" : "btn-nb-info"}`}
        >
          {busy
            ? "…"
            : subscribed
              ? t("push.turnOff", { defaultValue: "Turn off" })
              : t("push.turnOn", { defaultValue: "Turn on" })}
        </button>
      </div>
    </div>
  );
}
